/**
 * "3 minutes ago", in the reader's own language.
 *
 * `Intl.RelativeTimeFormat` handles the plural rules and the wording, which is
 * the part that is genuinely hard — Polish alone has three plural forms and
 * different ones again for "in" versus "ago". Hand-rolling this produces
 * "3 minuty temu" where it should say "3 minuty temu" only sometimes.
 */

const DIVISIONS: {
  limit: number;
  seconds: number;
  unit: Intl.RelativeTimeFormatUnit;
}[] = [
  { limit: 60, seconds: 1, unit: "second" },
  { limit: 3600, seconds: 60, unit: "minute" },
  { limit: 86_400, seconds: 3600, unit: "hour" },
  { limit: 604_800, seconds: 86_400, unit: "day" },
  { limit: 2_629_800, seconds: 604_800, unit: "week" },
  { limit: 31_557_600, seconds: 2_629_800, unit: "month" },
];

const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

export function relativeTime(
  value: string | Date,
  now: Date = new Date(),
): string {
  const then = value instanceof Date ? value : new Date(value);
  const elapsed = (then.getTime() - now.getTime()) / 1000;
  const magnitude = Math.abs(elapsed);

  // `numeric: "auto"` turns this into "now" / "teraz" rather than "0 seconds ago".
  if (magnitude < 45) return formatter.format(0, "second");

  for (const { limit, seconds, unit } of DIVISIONS) {
    if (magnitude < limit)
      return formatter.format(Math.round(elapsed / seconds), unit);
  }
  return formatter.format(Math.round(elapsed / 31_557_600), "year");
}

/** The full timestamp, for the `title` attribute behind the relative one. */
export function exactTime(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleString(undefined, {
    dateStyle: "full",
    timeStyle: "short",
  });
}
